import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';

const CartContext = createContext();

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within CartProvider');
  }
  return context;
};

export const CartProvider = ({ children }) => {
  // Initialize cart from localStorage synchronously to prevent flash
  const [cart, setCart] = useState(() => {
    try {
      const savedCart = localStorage.getItem('cart');
      return savedCart ? JSON.parse(savedCart) : [];
    } catch {
      return [];
    }
  });
  
  // Track recently added items for notification animation
  const [recentlyAdded, setRecentlyAdded] = useState(null);
  const [showNotification, setShowNotification] = useState(false);
  
  const isInitialized = useRef(false);

  // Mark as initialized after first render
  useEffect(() => {
    isInitialized.current = true;
  }, []);

  // Save cart to localStorage whenever it changes (but not on initial mount)
  useEffect(() => {
    if (isInitialized.current) {
      localStorage.setItem('cart', JSON.stringify(cart));
    }
  }, [cart]);

  // Clear notification after animation
  useEffect(() => {
    if (recentlyAdded) {
      setShowNotification(true);
      const timer = setTimeout(() => {
        setRecentlyAdded(null);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [recentlyAdded]);

  const addToCart = useCallback((product, quantity = 1) => {
    setCart(prevCart => {
      const existingItem = prevCart.find(item => item.id === product.id);
      
      if (existingItem) {
        return prevCart.map(item =>
          item.id === product.id
            ? { ...item, quantity: item.quantity + quantity }
            : item
        );
      }
      
      return [...prevCart, { ...product, quantity }];
    });
    
    // Set recently added for notification
    setRecentlyAdded({
      name: product.name,
      quantity,
      timestamp: Date.now()
    });
  }, []);

  const removeFromCart = useCallback((productId) => {
    setCart(prevCart => prevCart.filter(item => item.id !== productId));
  }, []);

  const updateQuantity = useCallback((productId, quantity) => {
    // Allow decimal quantities for bulk orders
    const validQuantity = parseFloat(quantity);
    
    if (validQuantity <= 0 || isNaN(validQuantity)) {
      removeFromCart(productId);
      return;
    }
    
    setCart(prevCart =>
      prevCart.map(item =>
        item.id === productId ? { ...item, quantity: validQuantity } : item
      )
    );
  }, [removeFromCart]);

  const clearCart = useCallback(() => {
    setCart([]);
    setShowNotification(false);
    setRecentlyAdded(null);
    localStorage.removeItem('cart');
  }, []);

  const getCartTotal = useCallback(() => {
    return cart.reduce((total, item) => total + (item.price * item.quantity), 0);
  }, [cart]);

  // Returns number of unique products in cart (for badge display)
  const getCartCount = useCallback(() => {
    return cart.length;
  }, [cart]);

  // Returns total quantity of all items (for display like "10 kg total")
  const getCartQuantity = useCallback(() => {
    return cart.reduce((count, item) => count + item.quantity, 0);
  }, [cart]);

  // Check if cart has items (for persistent notification)
  const hasItems = cart.length > 0;

  return (
    <CartContext.Provider
      value={{
        cart,
        addToCart,
        removeFromCart,
        updateQuantity,
        clearCart,
        getCartTotal,
        getCartCount,
        getCartQuantity,
        recentlyAdded,
        showNotification,
        hasItems
      }}
    >
      {children}
    </CartContext.Provider>
  );
};